import { ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild } from '@angular/core'
import { PlayerListenerComponentDirective } from '../../../base-component/player-listener.component'

@Component({
  selector: 'bmcore-progress-bar',
  templateUrl: './bmcore-progress-bar.component.html',
  styleUrls: ['./bmcore-progress-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BmcoreProgressBarComponent extends PlayerListenerComponentDirective<any> {
  @Input() showTimesInline = false
  @Input() showTimesTooltip = false
  @Input() isPlayerMobLgView = false

  @ViewChild('progressBar') progressBarRef!: ElementRef<HTMLDivElement>

  /**
   * Calculates the current position along the progress bar based on the user's interaction.
   * Supports both mouse and touch events.
   *
   * 1. Obtains the progress bar's bounding rectangle.
   * 2. Extracts the X coordinate of the interaction:
   *    - For MouseEvent: uses `clientX`
   *    - For TouchEvent:
   *       - First tries `touches[0]` during active touch
   *       - Falls back to `changedTouches[0]` for touchend events
   * 3. Computes offset within the progress bar and converts it to percentage.
   * 4. Clamps percentage to [0, 100] bounds.
   *
   * Returns:
   * - A percentage number between 0 and 100 representing the seek position.
   * - If the progress bar element is not available, returns the current `progress` as a fallback.
   */
  protected override getSeekPercent(event: MouseEvent | TouchEvent): number {
    const progressBar = this.progressBarRef?.nativeElement
    if (!progressBar) {
      return this.progress
    }

    const rect = progressBar.getBoundingClientRect()

    let clientX = 0
    if (event instanceof MouseEvent) {
      clientX = event.clientX
    } else if (event.touches?.length) {
      clientX = event.touches[0].clientX
    } else if ((event as TouchEvent).changedTouches?.length) {
      clientX = (event as TouchEvent).changedTouches[0].clientX
    }

    const offsetX = clientX - rect.left
    const percent = (offsetX / rect.width) * 100

    return Math.min(100, Math.max(0, percent))
  }
}
